import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface TocItem {
  id: string
  text: string
  level: 2 | 3
}

interface DocsToCProps {
  content: string
}

function extractHeadings(markdown: string): Array<TocItem> {
  if (typeof markdown !== 'string') return []

  const lines = markdown.split('\n')
  const items: Array<TocItem> = []

  for (const line of lines) {
    const h2 = line.match(/^##\s+(.+)$/)
    const h3 = line.match(/^###\s+(.+)$/)
    if (h2) {
      const text = h2[1].trim()
      items.push({ id: slugify(text), text, level: 2 })
    } else if (h3) {
      const text = h3[1].trim()
      items.push({ id: slugify(text), text, level: 3 })
    }
  }

  return items
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

export function DocsToC({ content }: DocsToCProps) {
  const headings = extractHeadings(content)
  const [activeId, setActiveId] = useState<string>('')
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (headings.length === 0) return

    observerRef.current?.disconnect()

    const handleIntersect = (entries: Array<IntersectionObserverEntry>) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id)
        }
      }
    }

    observerRef.current = new IntersectionObserver(handleIntersect, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0,
    })

    // Small delay to let MDX render headings into the DOM
    const timer = setTimeout(() => {
      for (const h of headings) {
        const el = document.getElementById(h.id)
        if (el) observerRef.current?.observe(el)
      }
    }, 200)

    return () => {
      clearTimeout(timer)
      observerRef.current?.disconnect()
    }
  }, [content]) // re-run when content changes

  if (headings.length === 0) return null

  return (
    <aside className="w-52 shrink-0 hidden xl:block">
      <div className="sticky top-20">
        <p className="text-xs font-semibold uppercase tracking-widest text-[var(--color-text-tertiary)] mb-3 px-3">
          On this page
        </p>
        <ul className="space-y-0.5">
          {headings.map((h) => (
            <li key={h.id}>
              <a
                href={`#${h.id}`}
                onClick={(e) => {
                  e.preventDefault()
                  document
                    .getElementById(h.id)
                    ?.scrollIntoView({ behavior: 'smooth' })
                  setActiveId(h.id)
                }}
                className={cn(
                  'block rounded-full px-3 py-1.5 text-sm leading-snug transition-all duration-300 ease-brisk',
                  h.level === 3 && 'pl-6',
                  activeId === h.id
                    ? 'bg-primary/10 font-medium text-primary'
                    : 'text-[var(--color-text-tertiary)] hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-secondary)]',
                )}
              >
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  )
}
